import { useState, useEffect } from 'react';
import { featuresService } from '../services/features.service';
import { useEmpresa } from '../contexts/EmpresaContext';

interface UseFeatureAccessReturn {
  features: string[];
  modulos: string[];
  hasFeature: (featureKey: string) => boolean;
  hasModulo: (moduloKey: string) => boolean;
  hasAnyFeature: (featureKeys: string[]) => boolean;
  hasAllFeatures: (featureKeys: string[]) => boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook para verificar acesso a features e módulos baseado no contrato/subscription
 */
export function useFeatureAccess(): UseFeatureAccessReturn {
  const { empresa } = useEmpresa();
  const [features, setFeatures] = useState<string[]>([]);
  const [modulos, setModulos] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadFeatures = async (forceRefresh: boolean = false) => {
    setIsLoading(true);
    try {
      const [fetchedFeatures, fetchedModulos] = await Promise.all([
        featuresService.getFeatures(forceRefresh),
        featuresService.getModulos(forceRefresh),
      ]);
      setFeatures(Array.isArray(fetchedFeatures) ? fetchedFeatures : []);
      setModulos(Array.isArray(fetchedModulos) ? fetchedModulos : []);
    } catch (error) {
      console.error('❌ Erro ao carregar features e módulos:', error);
      setFeatures([]);
      setModulos([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFeatures(false);
  }, [empresa?.id]);

  const hasFeature = (featureKey: string): boolean => {
    if (!features || !Array.isArray(features)) {
      return false;
    }
    return features.includes(featureKey);
  };

  const hasModulo = (moduloKey: string): boolean => {
    if (!modulos || !Array.isArray(modulos)) {
      return false;
    }
    return modulos.includes(moduloKey);
  };

  const hasAnyFeature = (featureKeys: string[]): boolean => {
    if (!features || !Array.isArray(features)) {
      return false;
    }
    return featureKeys.some(key => features.includes(key));
  };

  const hasAllFeatures = (featureKeys: string[]): boolean => {
    if (!features || !Array.isArray(features)) {
      return false;
    }
    return featureKeys.every(key => features.includes(key));
  };

  const refresh = async (): Promise<void> => {
    await loadFeatures(true);
  };

  return {
    features,
    modulos,
    hasFeature,
    hasModulo,
    hasAnyFeature,
    hasAllFeatures,
    isLoading,
    refresh,
  };
}

